import { isEmpty, omit, omitBy } from 'lodash';

import type {
  SlaRuleCreateRequest,
  SlaRuleCreateRequestSlaActionType,
  SlaRulePreviewRequest,
} from '~/server_shared/openapi/data-contracts';

import type { SlaRuleForDialog, SlaTemplateParameter } from '../types';

export type SlaActionTypeValue = `${SlaRuleCreateRequestSlaActionType}`;

export interface ActionFormValues {
  action_type: SlaActionTypeValue | '';
  recipient: string;
  generic_template_id: number | '';
  // Not edited by the dialog; carried so an edit resubmits what is stored.
  category_id: number | null;
  config_json: Record<string, unknown> | null;
}

export interface ThresholdFormValues {
  sla_days: number | '';
  repeat_every_days: number | '';
  actions: ActionFormValues[];
}

export interface SlaRuleFormValues {
  display_name: string;
  metabase_card_id: number | '';
  /** Keyed by the card parameter's slug, holding exactly what the rule stores. */
  context_params: Record<string, unknown>;
  is_business_days: boolean;
  /** Not a field in the dialog: the table's switch owns it. Carried because the update body defaults it to false. */
  is_enabled: boolean;
  thresholds: ThresholdFormValues[];
}

/** The engine binds this per run from the thresholds; a rule may not pin it. */
export const SLA_DAYS_PARAM_SLUG = 'sla_days';

export const EMPTY_ACTION: ActionFormValues = {
  action_type: '',
  recipient: '',
  generic_template_id: '',
  category_id: null,
  config_json: null,
};

export const EMPTY_THRESHOLD: ThresholdFormValues = { sla_days: '', repeat_every_days: 1, actions: [EMPTY_ACTION] };

export const isEmptySelection = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

export const toFormValues = (rule?: SlaRuleForDialog): SlaRuleFormValues => ({
  display_name: rule?.display_name ?? '',
  metabase_card_id: rule?.metabase_card_id ?? '',
  // Verbatim: never rebuilt from the DTO's coerced sub_organization_ids / lobs.
  context_params: { ...(rule?.context_params ?? {}) },
  is_business_days: rule?.is_business_days ?? false,
  is_enabled: rule?.is_enabled ?? false,
  thresholds: rule
    ? rule.thresholds.map((threshold) => ({
        sla_days: threshold.sla_days,
        repeat_every_days: threshold.repeat_every_days,
        actions: threshold.actions.map((action) => ({
          action_type: action.action_type as SlaActionTypeValue,
          recipient: action.recipient,
          generic_template_id: action.generic_template_id ?? '',
          category_id: action.category_id ?? null,
          config_json: action.config_json ?? null,
        })),
      }))
    : [EMPTY_THRESHOLD],
});

export const contextParameters = (parameters: SlaTemplateParameter[]): SlaTemplateParameter[] =>
  parameters.filter((parameter) => parameter.slug !== SLA_DAYS_PARAM_SLUG);

export const defaultContextParams = (parameters: SlaTemplateParameter[]): Record<string, unknown> =>
  Object.fromEntries(
    contextParameters(parameters)
      // `default` is typed `null` by the generator (Optional[Any]); it carries the card's value.
      .map((parameter) => [parameter.slug, parameter.default as unknown] as const)
      .filter(([, value]) => !isEmptySelection(value))
  );

// A cleared picker leaves the parameter unset, so the template's default applies: the key is dropped
// rather than sent as [].
const selectedContextParams = (contextParams: Record<string, unknown>): Record<string, unknown> =>
  omitBy(contextParams, isEmptySelection);

export const toWriteRequest = (values: SlaRuleFormValues): SlaRuleCreateRequest => ({
  display_name: values.display_name.trim(),
  metabase_card_id: Number(values.metabase_card_id),
  context_params: selectedContextParams(values.context_params),
  is_business_days: values.is_business_days,
  is_enabled: values.is_enabled,
  thresholds: values.thresholds.map((threshold) => ({
    sla_days: Number(threshold.sla_days),
    repeat_every_days: Number(threshold.repeat_every_days),
    actions: threshold.actions.map((action) => ({
      action_type: action.action_type as SlaRuleCreateRequestSlaActionType,
      recipient: action.recipient,
      generic_template_id: action.generic_template_id === '' ? null : action.generic_template_id,
      category_id: action.category_id,
      config_json: isEmpty(action.config_json) ? null : action.config_json,
    })),
  })),
});

export const toPreviewRequest = (values: SlaRuleFormValues, ruleId: number | null): SlaRulePreviewRequest => ({
  display_name: values.display_name.trim(),
  metabase_card_id: Number(values.metabase_card_id),
  rule_id: ruleId,
  context_params: selectedContextParams(values.context_params),
  is_business_days: values.is_business_days,
  thresholds: values.thresholds.map(({ sla_days, repeat_every_days }) => ({
    sla_days: Number(sla_days),
    repeat_every_days: Number(repeat_every_days),
  })),
});

/** Identity of what a preview answered, so the panel can drop a result once the config moves on. The
 *  rule's name is left out: the request carries it, but it cannot change the counts. */
export const previewKey = (values: SlaRuleFormValues): string =>
  JSON.stringify(omit(toPreviewRequest(values, null), 'display_name'));
